import React from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Chip,
} from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { useDriftDashboard } from '../hooks/useDriftDashboard';

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  'in-sync': 'success',
  'outdated': 'warning',
  'missing': 'error',
  'orphaned': 'error',
  'pending-termination': 'warning',
  'failed': 'error',
  'unknown': 'default',
};

const DriftOverviewPanel: React.FC = () => {
  const { data, loading, error } = useDriftDashboard();

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <InlineAlert severity="error">{error}</InlineAlert>;
  }

  if (!data) return null;

  const inSyncCount = data.by_status['in-sync'] || 0;
  const inSyncPct = data.total_deployments > 0
    ? Math.round((inSyncCount / data.total_deployments) * 100)
    : 0;
  const driftedCount = data.total_deployments - inSyncCount - (data.by_status['unknown'] || 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 3, flex: 1, textAlign: 'center' }}>
          <Typography variant="h3">{data.total_deployments}</Typography>
          <Typography variant="body2" color="text.secondary">Total Deployments</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 3, flex: 1, textAlign: 'center' }}>
          <Typography variant="h3" color="success.main">{inSyncPct}%</Typography>
          <Typography variant="body2" color="text.secondary">In Sync</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 3, flex: 1, textAlign: 'center' }}>
          <Typography variant="h3" color="error.main">{driftedCount}</Typography>
          <Typography variant="body2" color="text.secondary">Drifted</Typography>
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Status Breakdown</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(data.by_status).map(([status, count]) => (
            <Chip
              key={status}
              label={`${status}: ${count}`}
              color={STATUS_COLORS[status] || 'default'}
              variant="outlined"
            />
          ))}
        </Box>
      </Paper>

      <Paper variant="outlined">
        <Typography variant="h6" sx={{ p: 2, pb: 0 }}>Drift by Service</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Service</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">In Sync</TableCell>
                <TableCell align="right">Drifted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(data.by_service).map(([service, stats]) => (
                <TableRow key={service}>
                  <TableCell>{service}</TableCell>
                  <TableCell align="right">{stats.total}</TableCell>
                  <TableCell align="right">{stats.in_sync}</TableCell>
                  <TableCell align="right">{stats.drifted}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DriftOverviewPanel;
